import type { AppConfig, RootState } from '@frontend/core'
import { generateServerUrls, getStore, initSentry } from '@frontend/core'
import { createInitialRegionsState } from '@frontend/core/lib/domains/regions'
import { sportsConfig } from '@frontend/core/lib/domains/sports/config'
import { initialSportsState } from '@frontend/core/lib/domains/sports/slice'
import { initialAppState } from '@frontend/core/lib/modules/app'
import { initialRouterState } from '@frontend/core/lib/modules/router'
import { initialServersState } from '@frontend/core/lib/modules/servers'
import { SportableApp } from '@frontend/react/app/components'
import { loadAppServerConfig } from '@frontend/react/app/functions'
import type { JSX } from 'react'
import * as ReactDOM from 'react-dom/client'
import packageJson from '../../../../../package.json'

declare const __SENTRY_RELEASE__: string | undefined
const version =
  (typeof __SENTRY_RELEASE__ !== 'undefined' && __SENTRY_RELEASE__) ||
  packageJson.version

// Load in cloud run environment variables
export async function startApp(
  appConfig: AppConfig,
  CustomHeader?: (props: any) => JSX.Element,
  sentryConfig?: { dsn: string },
  isCloud = true
) {
  // If Sentry DSN is provided, initialize Sentry
  if (sentryConfig && sentryConfig.dsn) {
    initSentry(version, sentryConfig.dsn)
  }

  const appServerConfig = await loadAppServerConfig(appConfig.pitchSide)

  const initialRegionState = createInitialRegionsState(
    appServerConfig.publicEnvVars?.PUBLIC_AVAILABLE_REGIONS
  )

  const initialState: Partial<RootState> = {
    sports: {
      ...initialSportsState,
      sportsConfig
    },
    app: {
      ...initialAppState,
      language: 'en' as const,
      config: appConfig,
      isCloudApp: isCloud,
      version: appServerConfig.version || 'unknown'
    },
    authentication: {
      authenticated: false,
      lastValidityCheck: null
    },
    servers: {
      ...initialServersState,
      urls: generateServerUrls(
        initialServersState.serverMap,
        appServerConfig.publicEnvVars,
        appConfig
      )
    },
    router: {
      ...initialRouterState,
      initialRoute: appConfig.landingPath
    },
    regions: {
      ...initialRegionState
    }
  }

  // Create Redux store with initial state
  const store = getStore(initialState)

  // Get or create ReactDOM root
  const container = document.getElementById('root')
  if (!container) {
    throw new Error('Root element not found')
  }
  const root = ReactDOM.createRoot(container)

  // Render the app
  root.render(
    <SportableApp
      appConfig={appConfig}
      CustomHeader={CustomHeader}
      store={store}
    />
  )
}
